import { useAxiosStore } from '@risksmart-app/components/src/hooks/useAxios';
import { useCallback } from 'react';
import { useTRPCClient } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

/**
 * Re-sends a third-party contact's set-password email (COI-872).
 *
 * A flag-gated V2/V3 twin, hand-written rather than built with
 * `createMutationHook`: that factory pairs a tRPC mutation with an Apollo
 * GraphQL twin, and this write never existed in Apollo — V2 exposes it as the
 * REST route `POST /third-party-contact/resend-password-reset`. That is exactly
 * why it was missed by the GraphQL→tRPC tracker for so long: with no Apollo
 * import and no generated document, nothing enumerated it. The V2 leg stays as
 * the fallback for non-tRPC orgs — do not hard-switch it.
 *
 * Returns a callback rather than mutation state: it fires on a click, and the
 * calling modal owns its own in-flight flag and toasts (per this package's rule
 * that mutation hooks add no UI side-effects of their own).
 */
export const useResendThirdPartyPasswordReset = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpcClient = useTRPCClient();
  const { authorisedAxiosInstance } = useAxiosStore();

  return useCallback(
    async (contactId: string): Promise<void> => {
      if (trpcEnabled) {
        await trpcClient.frontend.thirdPartyContact.resendPasswordReset.mutate({
          ContactId: contactId,
        });

        return;
      }

      await authorisedAxiosInstance.post(
        '/third-party-contact/resend-password-reset',
        { ContactId: contactId }
      );
    },
    [trpcEnabled, trpcClient, authorisedAxiosInstance]
  );
};
